import { Children, Fragment, isValidElement, MouseEvent, ReactNode, useCallback, useId, useState } from 'react';

import Button from '@pelckmans/elementis/components/button';
import DropdownMenu from '@pelckmans/elementis/components/dropdown-menu';
import Options from '@pelckmans/elementis/icons/options';

function unwrapFragments(children: ReactNode): ReactNode[] {
  return Children.toArray(children).flatMap(child => {
    if (isValidElement<{ children?: ReactNode }>(child) && child.type === Fragment) {
      return unwrapFragments(child.props.children);
    }

    return child;
  });
}

export default function ActionMenuButton({
  children,
  disabled,
  zIndex,
  buttonProps,
  menuProps,
}: {
  children: (handleToggle: () => void) => React.ReactNode;
  disabled?: boolean;
  zIndex?: number;
  buttonProps: {
    ariaLabel: string;
  };
  menuProps: {
    ariaLabel: string;
  };
}) {
  const menuId = useId();

  const [buttonEl, setButtonEl] = useState<HTMLButtonElement | null>(null);

  const [open, setOpen] = useState(false);

  const handleToggle = useCallback((e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    e?.preventDefault();

    setOpen(prev => !prev);
  }, []);

  const closeMenu = useCallback(() => {
    setOpen(false);
    buttonEl?.focus();
  }, [buttonEl]);

  function handleCollapse(e?: Event) {
    if (!open || (e?.type === 'mousedown' && buttonEl?.contains(e.target as Node))) return;

    closeMenu();
  }

  return (
    <>
      <Button
        prominence="neutral"
        startIcon={<Options />}
        aria-label={buttonProps.ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={menuId}
        size="sm"
        onClick={handleToggle}
        ref={(el: HTMLButtonElement) => setButtonEl(el)}
        data-testid="action-menu-button"
        disabled={disabled}
      />
      {open && (
        <DropdownMenu
          actionEl={buttonEl}
          referenceEl={buttonEl}
          role="menu"
          menuProps={{
            ariaLabel: menuProps.ariaLabel,
            id: menuId,
          }}
          selectableByEnter
          onCollapse={handleCollapse}
          grow
          flip
          zIndex={zIndex}
          disableBackgroundScroll
        >
          {unwrapFragments(children(handleToggle))}
        </DropdownMenu>
      )}
    </>
  );
}
